import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { FilterDto } from '../util/types/FilterDto';

export interface UseFilterReturn {
	value: FilterDto;
	setDateRange: (startDate: Date, endDate: Date) => void;
	setPharmacy: (pharmacy: string | null) => void;
	isDateRangeValid: boolean;
	isFilterValid: boolean;
	isEndBeforeStart: boolean;
}

export function useFilter(): UseFilterReturn {
	const [searchParams, setSearchParams] = useSearchParams();

	const [value, setFilter] = useState<FilterDto>(() => {
		const pharmacy = searchParams.get('pharmacy');
		const start = searchParams.get('startDate');
		const end = searchParams.get('endDate');

		return {
			startDate: start ? new Date(start) : getSevenDaysAgo(),
			endDate: end ? new Date(end) : new Date(),
			pharmacyGroupName:
				pharmacy?.toLowerCase() === 'all' ? null : (pharmacy ?? null),
		};
	});

	// Write back to URL on change (skip first render — we already read from it)
	const isFirstRender = useRef(true);
	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);

				if (value.pharmacyGroupName) {
					next.set('pharmacy', value.pharmacyGroupName);
				} else {
					next.delete('pharmacy');
				}

				if (value.startDate) {
					next.set('startDate', value.startDate.toISOString());
				} else {
					next.delete('startDate');
				}

				if (value.endDate) {
					next.set('endDate', value.endDate.toISOString());
				} else {
					next.delete('endDate');
				}

				return next;
			},
			{ replace: true },
		);
	}, [value, setSearchParams]);

	const setDateRange = (startDate: Date, endDate: Date) => {
		setFilter((prev) => ({ ...prev, startDate, endDate }));
	};

	const setPharmacy = (pharmacy: string | null) => {
		setFilter((prev) => ({
			...prev,
			pharmacyGroupName: pharmacy?.toLowerCase() === 'all' ? null : pharmacy,
		}));
	};

	// biome-ignore lint/style/noNonNullAssertion: <explanation>
	const isEndBeforeStart = value.startDate! > value.endDate!;

	const isDateRangeValid =
		value.startDate != null && value.endDate != null && isEndBeforeStart;

	const isFilterValid = isDateRangeValid;

	return {
		value,
		setDateRange,
		setPharmacy,
		isDateRangeValid,
		isFilterValid,
		isEndBeforeStart,
	};
}

function getSevenDaysAgo() {
	const date = new Date();
	date.setDate(date.getDate() - 7);
	return date;
}
